import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
  DragOverlay,
} from "@dnd-kit/core";
import { Loader2, Calendar, AlertTriangle, GripVertical, RefreshCw, Sparkles, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { STATUSES, STATUS_TONE, formatRel, initials } from "./constants";
import DomainBanner from "./DomainBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminCrmPipelinePage() {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeCard, setActiveCard] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/crm/pipeline`);
      setColumns(data.columns);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const findCard = useCallback(
    (id) => {
      for (const col of columns) {
        const c = col.cards.find((x) => x.id === id);
        if (c) return { card: c, fromStatus: col.status };
      }
      return null;
    },
    [columns]
  );

  const onDragStart = (e) => {
    const found = findCard(e.active.id);
    if (found) setActiveCard(found.card);
  };

  const onDragEnd = async (e) => {
    setActiveCard(null);
    if (!e.over) return;
    const cardId = e.active.id;
    const toStatus = e.over.id;
    const found = findCard(cardId);
    if (!found || found.fromStatus === toStatus) return;
    if (!STATUSES.includes(toStatus)) return;

    // Optimistic update
    setColumns((prev) =>
      prev.map((col) => {
        if (col.status === found.fromStatus) {
          return { ...col, cards: col.cards.filter((c) => c.id !== cardId), count: col.count - 1 };
        }
        if (col.status === toStatus) {
          return { ...col, cards: [{ ...found.card, status: toStatus }, ...col.cards], count: col.count + 1 };
        }
        return col;
      })
    );

    try {
      await axios.post(`${API}/admin/crm/contacts/${cardId}/move`, {
        to_status: toStatus,
        from_status: found.fromStatus,
      });
      toast.success(`Moved → ${toStatus}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed — reverting");
      load();
    }
  };

  const onScoreChange = useCallback(async (cardId, patch) => {
    // Find the card so we can revert if save fails
    let original = null;
    setColumns((prev) =>
      prev.map((col) => ({
        ...col,
        cards: col.cards.map((c) => {
          if (c.id === cardId) {
            original = c;
            return { ...c, ...patch };
          }
          return c;
        }),
      }))
    );
    try {
      await axios.put(`${API}/admin/crm/contacts/${cardId}`, patch);
      // No toast — keep the UI quiet for a fast slider interaction
    } catch (err) {
      // Revert
      if (original) {
        setColumns((prev) =>
          prev.map((col) => ({
            ...col,
            cards: col.cards.map((c) => (c.id === cardId ? original : c)),
          }))
        );
      }
      toast.error(err?.response?.data?.detail || "Score save failed — reverted");
    }
  }, []);

  return (
    <div className="space-y-5" data-testid="crm-pipeline-page">
      <DomainBanner />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">CRM · Pipeline</h1>
          <p className="text-sm text-slate-500 mt-0.5">Drag a card between columns to update its status. Each move is auto-logged in the contact's activity feed.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="crm-pipeline-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading pipeline…</div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error}</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="crm-pipeline-board">
            {columns.map((col) => (
              <PipelineColumn
                key={col.status}
                status={col.status}
                count={col.count}
                cards={col.cards}
                onScoreChange={onScoreChange}
              />
            ))}
          </div>
          <DragOverlay>
            {activeCard ? <PipelineCard card={activeCard} dragging /> : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

function PipelineColumn({ status, count, cards, onScoreChange }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const tone = STATUS_TONE[status] || "bg-slate-100 text-slate-700 ring-slate-200";

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-72 rounded-2xl bg-slate-50 border-2 transition-colors ${isOver ? "border-cyan-400 bg-cyan-50/40" : "border-transparent"}`}
      data-testid={`crm-pipeline-column-${status}`}
    >
      <div className="px-3 py-2.5 flex items-center justify-between border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md ring-1 ${tone}`}>
            {status}
          </span>
        </div>
        <span className="text-xs font-semibold text-slate-500 tabular-nums" data-testid={`crm-pipeline-count-${status}`}>
          {count}
        </span>
      </div>
      <div className="p-2 space-y-2 min-h-[200px]">
        {cards.length === 0 ? (
          <div className="text-[11px] text-slate-400 text-center py-8">Drop cards here</div>
        ) : (
          cards.map((c) => <PipelineCard key={c.id} card={c} onScoreChange={onScoreChange} />)
        )}
      </div>
    </div>
  );
}

function PipelineCard({ card, dragging = false, onScoreChange }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: card.id });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;
  const overdue = card.next_follow_up_at && new Date(card.next_follow_up_at) < new Date();

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-xl border border-slate-200 p-3 shadow-sm hover:shadow-md transition-all ${
        isDragging ? "opacity-30" : ""
      } ${dragging ? "shadow-2xl rotate-2 ring-2 ring-cyan-400" : ""}`}
      data-testid={`crm-pipeline-card-${card.id}`}
    >
      <div className="flex items-start gap-2">
        <button
          {...attributes}
          {...listeners}
          className="text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing pt-0.5"
          aria-label="Drag card"
          data-testid={`crm-pipeline-card-handle-${card.id}`}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500/20 to-indigo-500/20 ring-1 ring-cyan-200 flex items-center justify-center text-[10px] font-semibold text-cyan-800 flex-shrink-0">
              {initials(card.name)}
            </div>
            <Link
              to={`/admin/crm/contacts/${card.id}`}
              className="text-sm font-semibold text-slate-900 hover:text-cyan-700 truncate"
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {card.name}
            </Link>
          </div>
          {card.organization && (
            <div className="text-xs text-slate-500 truncate mt-1">{card.organization}</div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-1">
            <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700">
              {card.category}
            </span>
            {(card.tags || []).slice(0, 2).map((t) => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-md bg-slate-50 text-slate-500">
                #{t}
              </span>
            ))}
          </div>
          {/* Inline score sliders */}
          {!dragging && onScoreChange && (
            <div className="mt-2 space-y-1.5" data-testid={`crm-pipeline-card-scores-${card.id}`}>
              <ScoreSlider
                label="Lead"
                icon={Sparkles}
                color="indigo"
                value={card.lead_score ?? 0}
                onChange={(v) => onScoreChange(card.id, { lead_score: v })}
                testid={`crm-card-lead-${card.id}`}
              />
              <ScoreSlider
                label="Priority"
                icon={Flame}
                color="rose"
                value={card.priority_score ?? 0}
                onChange={(v) => onScoreChange(card.id, { priority_score: v })}
                testid={`crm-card-priority-${card.id}`}
              />
            </div>
          )}
          <div className="mt-2 flex items-center gap-2 text-[10px] text-slate-500">
            {overdue ? (
              <span className="inline-flex items-center gap-1 text-rose-600 font-semibold">
                <AlertTriangle className="w-3 h-3" /> follow-up overdue
              </span>
            ) : card.next_follow_up_at ? (
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {new Date(card.next_follow_up_at).toLocaleDateString()}
              </span>
            ) : null}
            <span className="ml-auto text-slate-400">{formatRel(card.updated_at)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScoreSlider({ label, icon: Icon, color, value, onChange, testid }) {
  const [local, setLocal] = useState(value);
  // Sync external changes (e.g. after server save reload)
  useEffect(() => { setLocal(value); }, [value]);

  const tones = {
    indigo: "accent-indigo-600 bg-indigo-50 text-indigo-700",
    rose: "accent-rose-600 bg-rose-50 text-rose-700",
  };

  // Stop drag propagation on the slider itself
  const stop = (e) => { e.stopPropagation(); };

  return (
    <div
      className="flex items-center gap-2"
      onPointerDown={stop}
      onMouseDown={stop}
      onClick={stop}
    >
      <Icon className={`w-3 h-3 flex-shrink-0 ${tones[color].split(" ")[2]}`} />
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-12">{label}</span>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={local}
        onChange={(e) => setLocal(Number(e.target.value))}
        onPointerUp={() => { if (local !== value) onChange(local); }}
        onTouchEnd={() => { if (local !== value) onChange(local); }}
        onKeyUp={(e) => { if (e.key === "ArrowLeft" || e.key === "ArrowRight") { if (local !== value) onChange(local); } }}
        className={`flex-1 h-1 cursor-pointer ${tones[color].split(" ")[0]}`}
        aria-label={`${label} score`}
        data-testid={testid}
      />
      <span className={`text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-md ring-1 ring-${color}-200 ${tones[color].split(" ").slice(1, 3).join(" ")} w-9 text-center`}>
        {local}
      </span>
    </div>
  );
}
